import { formatNumberWithComma } from '../../../../utils/formatText';
import {
  IChartSettings,
  ILineMetaNewRetention,
  IParsedChartDataRecord,
  IParsedChartDataRetention,
  IRetentionOverTimeRow,
  IRetentionStandardRow,
} from '../../interfaces';
import * as RetentionCellPopupUI from './RetentionCellTooltipUI';
import { RetentionTooltipHeader } from './RetentionTooltipHeader';
import {
  defaultNumberFormatOptions,
  globalFormat,
} from '../../../../utils/formatting';
import { IRetentionTooltipProps } from '../../hooks/tooltip/useRetentionTooltip';
import { useOnClickOutside } from '../../../../utils/hooks';
import { useCallback, useRef } from 'react';
import * as SingleChartDataTooltipUI from '../../SingleChartDataTooltip/SingleChartDataTooltipUI';
import { ITooltipProps } from '../../hooks/tooltip/useSingleChartDataTooltip';
import { assertNotNil } from '../../../../utils/types';
import { DATA_KEY } from '../../constants/utils';
import { MaybeNil } from '../../../../utils/interfaces';
import { bootstrapTimezoneSelector } from '../../../../redux/selectors/bootstrap';
import { useAppSelector } from '../../../../redux/hooks';
import moment from 'moment';
import { useFloatingLayerContext } from '../../../FloatingManager';
import SingleChartDataTooltipMenuItem from '../../SingleChartDataTooltip/SingleChartDataTooltipMenuItem';
import { isScratchpadAnalysisId } from '../../../../redux/utils/transientAnalysis';
import { createPortal } from 'react-dom';
import { CHART_TYPES } from '../../constants/chartTypes';
import { useChartContext } from '../../ChartContext';
interface IProps {
  analysisId: string;
  tooltipProps: IRetentionTooltipProps;
  isTooltipLocked: boolean;
  clearLockedTooltipProps: () => void;
  // if not provided, use sliceBackground color in RetentionLine and RetentionBar chart
  cellBackgroundColor?: string;
  handleCreateCohort: (
    tooltipProps: IRetentionTooltipProps,
    isExecuteAfterRedirect: boolean,
  ) => void;
  position?: {
    x: number;
    y: number;
  };
}

export const generateRetentionTooltips = ({
  tooltipProps,
  linesMeta,
  chartData,
  data,
  chartSettings,
}: {
  tooltipProps: MaybeNil<ITooltipProps>;
  linesMeta: ILineMetaNewRetention;
  chartData: IParsedChartDataRetention;
  data: IParsedChartDataRecord[];
  chartSettings: IChartSettings;
}) => {
  if (!tooltipProps) {
    return { retentionTooltipProps: null, positionProps: null };
  }
  const lineMeta = linesMeta[tooltipProps.line.name];
  assertNotNil(lineMeta);

  const { table } = chartData;
  const { chartType, isChurn } = chartSettings;
  // find out returnCell and returnIndex
  // returnIndex is the index of days in the row
  const returnIndex = data.findIndex((d) => d[DATA_KEY] === tooltipProps.date);

  const selectedRow = (
    table.rows as (IRetentionStandardRow | IRetentionOverTimeRow)[]
  ).find(
    (row: IRetentionStandardRow | IRetentionOverTimeRow) =>
      row.key === lineMeta.name,
  );
  assertNotNil(selectedRow);
  const returnCell = selectedRow.returns[returnIndex];
  const retentionTooltipProps = {
    key: lineMeta.name,
    returnCell,
    sliceKey: lineMeta.sliceKey,
    isChurn,
    returnIndex,
    chartType,
  };
  const positionProps = {
    x: tooltipProps.x + 30,
    y: tooltipProps.y + 30,
  };
  return { retentionTooltipProps, positionProps };
};

// only RetentionLine and RetentionBar chart need createPortal
const PortalWrapper = ({
  children,
  chartType,
}: {
  children: React.ReactNode;
  chartType: CHART_TYPES;
}) => {
  if (chartType === CHART_TYPES.RETENTION) {
    return <>{children}</>;
  }
  return createPortal(children, document.body);
};

export default ({
  analysisId,
  tooltipProps,
  isTooltipLocked,
  clearLockedTooltipProps,
  cellBackgroundColor,
  handleCreateCohort,
  position,
}: IProps) => {
  const orgTimezone = useAppSelector(bootstrapTimezoneSelector);
  assertNotNil(orgTimezone);
  const {
    settings: { chartSettings },
    displayedSlices,
    metadata: { query, isEmbedded },
  } = useChartContext();
  const { chartType } = chartSettings;
  const { retentionCount, churnCount, retentionPercent, churnPercent } =
    tooltipProps.returnCell;
  const count = tooltipProps.isChurn ? churnCount : retentionCount;
  const percent = tooltipProps.isChurn ? churnPercent : retentionPercent;

  const formattedPercent = globalFormat.formatNumber(
    percent ?? 0,
    defaultNumberFormatOptions.percent,
  );
  const formattedCount = formatNumberWithComma(count);
  const containerRef = useRef<HTMLDivElement | null>(null);
  useOnClickOutside(containerRef, clearLockedTooltipProps);

  const zIndex = useFloatingLayerContext();
  const handleCreateCohortClick = useCallback(() => {
    handleCreateCohort(tooltipProps, false);
    clearLockedTooltipProps();
  }, [handleCreateCohort, clearLockedTooltipProps, tooltipProps]);
  const handleInspectUsersClick = useCallback(() => {
    handleCreateCohort(tooltipProps, true);
    clearLockedTooltipProps();
  }, [handleCreateCohort, clearLockedTooltipProps, tooltipProps]);

  const isScratchpadAnalysis = isScratchpadAnalysisId(analysisId);

  // identify if it's a cohort average row
  const hideMenuItems =
    tooltipProps.returnCell.__typename === 'IRetentionAverageReturnCell' ||
    isScratchpadAnalysis ||
    isEmbedded ||
    // disable items if the schema is disabled
    query.schema.disabled;

  const ContentWrapper = ({ children }: { children: React.ReactNode }) => {
    if (position) {
      return (
        <SingleChartDataTooltipUI.PositionedContainer
          ref={containerRef}
          style={{ top: position.y, left: position.x, zIndex }}
        >
          <SingleChartDataTooltipUI.TooltipContainer>
            {children}
          </SingleChartDataTooltipUI.TooltipContainer>
        </SingleChartDataTooltipUI.PositionedContainer>
      );
    }
    return (
      <RetentionCellPopupUI.PopupContainer ref={containerRef}>
        {children}
      </RetentionCellPopupUI.PopupContainer>
    );
  };

  return (
    <PortalWrapper chartType={chartType}>
      <ContentWrapper>
        <SingleChartDataTooltipUI.DataFrame $color={cellBackgroundColor ?? ''}>
          <SingleChartDataTooltipUI.HeaderRow>
            <RetentionTooltipHeader
              sliceKey={tooltipProps.sliceKey}
              displayedSlices={displayedSlices}
              returnCell={tooltipProps.returnCell}
            />
          </SingleChartDataTooltipUI.HeaderRow>
          <SingleChartDataTooltipUI.SubLabel>
            {tooltipProps.returnCell.returnPeriod.name}{' '}
            {moment.tz(orgTimezone)?.zoneAbbr()}
          </SingleChartDataTooltipUI.SubLabel>
          <SingleChartDataTooltipUI.ValueLabel>
            {formattedCount}
          </SingleChartDataTooltipUI.ValueLabel>
          <SingleChartDataTooltipUI.ValueLabel>
            {formattedPercent}
            <SingleChartDataTooltipUI.SubLabel>
              {tooltipProps.isChurn ? 'Churn' : 'Retention'}
            </SingleChartDataTooltipUI.SubLabel>
          </SingleChartDataTooltipUI.ValueLabel>
        </SingleChartDataTooltipUI.DataFrame>

        {!hideMenuItems ? (
          isTooltipLocked ? (
            <SingleChartDataTooltipUI.MenuContainer>
              <SingleChartDataTooltipMenuItem
                iconName="users-three"
                label={'Create Cohort'}
                onClick={handleCreateCohortClick}
              />
              <SingleChartDataTooltipMenuItem
                iconName="user-circle"
                label={'Inspect Users'}
                onClick={handleInspectUsersClick}
              />
            </SingleChartDataTooltipUI.MenuContainer>
          ) : (
            <SingleChartDataTooltipUI.ActionHint>
              Click for actions
            </SingleChartDataTooltipUI.ActionHint>
          )
        ) : null}
      </ContentWrapper>
    </PortalWrapper>
  );
};
